import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Trash2, Search, ShieldCheck, Receipt, Clock, Eye, Sparkles, CalendarX2, MailCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CleanStartDialog from "@/components/admin/CleanStartDialog";
import CleanupBeforeDialog from "@/components/admin/CleanupBeforeDialog";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const daysLeft = (iso) => Math.ceil((new Date(iso) - Date.now()) / 86_400_000);

const FILTERS = [
  { key: "all", label: "Alles", fn: () => true },
  { key: "trial", label: "Proef", fn: (u) => u.tier === "TRIAL" && !u.trial_expired },
  { key: "expired", label: "Proef verlopen", fn: (u) => u.tier === "TRIAL" && u.trial_expired },
  { key: "paid", label: "Betalend", fn: (u) => u.subscription_status === "active" || u.paid_orders > 0 },
  { key: "admin", label: "Beheerders", fn: (u) => u.role === "admin" },
  { key: "unverified", label: "Niet bevestigd", fn: (u) => u.email_verified === false },
];

function TierBadge({ u }) {
  if (u.role === "admin") return <span className="px-1.5 py-0.5 bg-foreground text-background text-[10px] tracking-widest font-bold inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> ADMIN</span>;
  const cls = { TRIAL: "bg-secondary/20 text-secondary", STANDARD: "bg-primary/15 text-primary", PREMIUM: "bg-positive/30 text-foreground", FREE: "bg-muted text-muted-foreground" }[u.tier] || "bg-muted text-muted-foreground";
  return <span className={`px-1.5 py-0.5 text-[10px] tracking-widest font-bold ${cls}`}>{u.tier || "—"}</span>;
}

function TrialCell({ u }) {
  if (u.tier !== "TRIAL" || !u.trial_ends_at) return <span className="text-muted-foreground">—</span>;
  const d = daysLeft(u.trial_ends_at);
  return u.trial_expired
    ? <span className="text-destructive font-bold">verlopen {fmtDate(u.trial_ends_at)}</span>
    : <span>{fmtDate(u.trial_ends_at)} <span className="text-muted-foreground">· nog {d} dag{d === 1 ? "" : "en"}</span></span>;
}

function ConfirmDialog({ count, emails, busy, onCancel, onConfirm }) {
  return (
    <Dialog open={count > 0} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-lg border-2 border-foreground rounded-none heavy-shadow" data-testid="users-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight pr-8">{count} account{count === 1 ? "" : "s"} definitief verwijderen?</DialogTitle>
          <DialogDescription>Clubs, scenario's, historische cijfers en teamlidmaatschappen gaan mee. Dit kan niet ongedaan worden gemaakt.</DialogDescription>
        </DialogHeader>
        <div className="max-h-40 overflow-y-auto border-2 border-foreground bg-muted/30 p-2 text-xs font-mono-num space-y-0.5" data-testid="users-delete-list">
          {emails.map((e) => <div key={e}>{e}</div>)}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} data-testid="users-delete-cancel" className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs">Annuleren</button>
          <button onClick={onConfirm} disabled={busy} data-testid="users-delete-confirm" className="btn-brutal bg-destructive text-white px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Verwijderen
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WarningPreviewDialog({ open, onClose }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    api.get("/admin/trial-cleanup/preview").then((r) => setPreview(r.data))
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail) || "Voorbeeld laden mislukt."); onClose(); });
  }, [open, onClose]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-2 border-foreground rounded-none heavy-shadow" data-testid="users-warning-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl uppercase tracking-tight pr-8">Voorbeeld waarschuwingsmail</DialogTitle>
          <DialogDescription>
            {preview ? `Verstuurd op dag ${preview.sent_on_day} na het proefeinde; het account wordt op dag ${preview.deleted_on_day} gewist. De datum in de mail is de echte verwijderdatum van de ontvanger.` : "Laden…"}
          </DialogDescription>
        </DialogHeader>
        {preview && (
          <>
            <div className="text-xs border-2 border-foreground bg-muted/30 px-3 py-2" data-testid="users-warning-preview-subject"><span className="stat-label mr-2">Onderwerp</span>{preview.subject}</div>
            <iframe title="Voorbeeld waarschuwingsmail" srcDoc={preview.html} sandbox="" data-testid="users-warning-preview-frame"
              className="w-full h-[480px] border-2 border-foreground bg-white" />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UsersBlock() {
  const [users, setUsers] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [cleanStartOpen, setCleanStartOpen] = useState(false);
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const closePreview = useCallback(() => setPreviewOpen(false), []);

  const verifyUser = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/verify-email`);
      toast.success(`${u.email} bevestigd`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const load = useCallback(async () => {
    try { setUsers((await api.get("/admin/users")).data.users || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Laden mislukt."); setUsers([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const fn = FILTERS.find((f) => f.key === filter)?.fn || (() => true);
    const needle = q.trim().toLowerCase();
    return (users || []).filter(fn).filter((u) => !needle || `${u.email} ${u.name || ""} ${u.relation_code || ""}`.toLowerCase().includes(needle));
  }, [users, filter, q]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, (users || []).filter(f.fn).length])), [users]);
  const visibleDeletable = visible.filter((u) => u.deletable);
  const allVisibleSelected = visibleDeletable.length > 0 && visibleDeletable.every((u) => selected.has(u.id));

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    visibleDeletable.forEach((u) => (allVisibleSelected ? n.delete(u.id) : n.add(u.id)));
    return n;
  });

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const ids = pending.map((u) => u.id);
      const { data } = ids.length === 1 ? await api.delete(`/admin/users/${ids[0]}`) : await api.post("/admin/users/bulk-delete", { user_ids: ids });
      const n = ids.length === 1 ? 1 : data.deleted;
      toast.success(`${n} account${n === 1 ? "" : "s"} verwijderd.`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} overgeslagen: ${[...new Set(data.skipped.map((s) => s.reason))].join(" · ")}`);
      setSelected(new Set()); setPending([]); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Verwijderen mislukt."); }
    setBusy(false);
  };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/admin/trial-cleanup/run");
      toast.success(`Opschoning gedraaid: ${data.warned} gewaarschuwd, ${data.purged} verwijderd${data.skipped ? `, ${data.skipped} overgeslagen` : ""}.`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Opschoning mislukt."); }
    setCleaning(false);
  };

  const input = "border-2 border-foreground bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary";
  return (
    <section className="bg-card border-2 border-foreground heavy-shadow overflow-x-auto" data-testid="users-block">
      <div className="p-5 border-b-2 border-foreground flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-black text-2xl uppercase tracking-tight">Gebruikers</h2>
          <p className="text-xs text-muted-foreground mt-1">Alle accounts · {users ? users.length : "…"} totaal. Accounts met betaalde facturen wis je via Crediteren + AVG-verwijdering.</p>
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="users-cleanup-note">
            <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />Verlopen proefaccounts krijgen op dag 83 een waarschuwingsmail en worden 90 dagen na het proefeinde automatisch gewist (dagelijks rond 06:00 NL-tijd).
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setPreviewOpen(true)} data-testid="users-warning-preview-btn" title="Bekijk de waarschuwingsmail die proefgebruikers op dag 83 ontvangen"
            className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Voorbeeld mail
          </button>
          <button onClick={runCleanup} disabled={cleaning} data-testid="users-cleanup-run-btn" title="Draait de dagelijkse opschoning nu (waarschuwen + wissen)"
            className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
            {cleaning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />} Opschoning nu
          </button>
          <button onClick={() => setPending(visible.filter((u) => selected.has(u.id)))} disabled={selected.size === 0} data-testid="users-bulk-delete-btn"
            className="btn-brutal bg-destructive text-white px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-40">
            <Trash2 className="w-3.5 h-3.5" /> Verwijder geselecteerde ({selected.size})
          </button>
          <button onClick={() => setCleanStartOpen(true)} data-testid="users-clean-start-btn" title="Alle testaccounts, uitnodigingen, kortingscodes en testbestellingen wissen vóór livegang"
            className="btn-brutal bg-foreground text-background px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Schone start
          </button>
          <button onClick={() => setCleanupOpen(true)} data-testid="users-cleanup-before-btn" title="Alle niet-beheerdersaccounts en systeemdata van vóór een datum wissen (betaalde accounts blijven staan)"
            className="btn-brutal bg-background text-foreground px-3 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5">
            <CalendarX2 className="w-3.5 h-3.5" /> Opschonen tot datum
          </button>
        </div>
      </div>
      <CleanStartDialog open={cleanStartOpen} onClose={() => setCleanStartOpen(false)} onDone={() => { setSelected(new Set()); load(); }} />
      <CleanupBeforeDialog open={cleanupOpen} onClose={() => setCleanupOpen(false)} onDone={() => { setSelected(new Set()); load(); }} />
      <div className="px-5 py-3 border-b-2 border-foreground bg-muted/30 flex items-center gap-2 flex-wrap" data-testid="users-filter-bar">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`users-filter-${f.key}`} aria-pressed={filter === f.key}
            className={`px-2.5 py-1 border-2 border-foreground font-display uppercase font-bold text-xs ${filter === f.key ? "bg-foreground text-background" : "bg-background text-foreground"}`}>
            {f.label} <span className="font-mono-num opacity-70">{counts[f.key] ?? 0}</span>
          </button>
        ))}
        <label className="ml-auto inline-flex items-center gap-1 text-xs"><Search className="w-3.5 h-3.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Zoek e-mail / naam / code" data-testid="users-search" className={`${input} w-56`} />
        </label>
      </div>
      {users === null ? <div className="p-8 text-center text-muted-foreground text-sm">Laden…</div> : visible.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground text-sm" data-testid="users-empty">Geen accounts in deze selectie.</div>
      ) : (
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="bg-foreground text-background">
              <th className="px-3 py-2 w-8"><input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} disabled={visibleDeletable.length === 0} data-testid="users-select-all" aria-label="Alles selecteren" /></th>
              {["Account", "Tier", "Proefperiode", "Abonnement", "Clubs", "Facturen", "Aangemaakt", ""].map((h, i) => (
                <th key={i} className={`px-3 py-2 font-display uppercase text-xs tracking-widest text-left ${i === 7 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className={selected.has(u.id) ? "bg-destructive/10" : ""}>
                <td className="px-3 py-2"><input type="checkbox" checked={selected.has(u.id)} onChange={() => toggle(u.id)} disabled={!u.deletable} data-testid={`user-select-${u.id}`} aria-label={`Selecteer ${u.email}`} /></td>
                <td className="px-3 py-2">
                  <div className="font-bold font-mono-num text-xs break-all">{u.email}</div>
                  <div className="text-xs text-muted-foreground">{u.name || "—"}{u.relation_code && <span className="ml-2 font-mono-num">{u.relation_code}</span>}{u.anonymized && <span className="ml-2 text-destructive">geanonimiseerd</span>}{u.email_verified === false && <span className="ml-2 text-secondary font-bold" data-testid={`user-unverified-${u.id}`}>e-mail niet bevestigd</span>}</div>
                </td>
                <td className="px-3 py-2"><TierBadge u={u} /></td>
                <td className="px-3 py-2 text-xs"><TrialCell u={u} /></td>
                <td className="px-3 py-2 text-xs">{u.subscription_status ? <span className={u.subscription_status === "active" ? "text-positive font-bold" : "text-muted-foreground"}>{u.subscription_status}</span> : "—"}</td>
                <td className="px-3 py-2 font-mono-num text-xs">{u.clubs}</td>
                <td className="px-3 py-2 font-mono-num text-xs">{u.paid_orders > 0 ? <span className="inline-flex items-center gap-1 font-bold"><Receipt className="w-3 h-3" /> {u.paid_orders}</span> : "0"}</td>
                <td className="px-3 py-2 font-mono-num text-xs">{fmtDate(u.created_at)}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {u.email_verified === false && (
                    <button onClick={() => verifyUser(u)} data-testid={`user-verify-${u.id}`} title="E-mailadres handmatig bevestigen (proef start nu)"
                      className="btn-brutal bg-background text-primary px-2.5 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 mr-2">
                      <MailCheck className="w-3.5 h-3.5" /> Bevestig
                    </button>
                  )}
                  <button onClick={() => setPending([u])} disabled={!u.deletable} data-testid={`user-delete-${u.id}`}
                    title={u.role === "admin" ? "Beheerders kun je hier niet verwijderen" : u.paid_orders > 0 ? "Heeft betaalde facturen — gebruik Crediteren + AVG-verwijdering" : "Account verwijderen"}
                    className="btn-brutal bg-background text-destructive px-2.5 py-1.5 font-display uppercase font-bold text-xs inline-flex items-center gap-1.5 disabled:opacity-30">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <WarningPreviewDialog open={previewOpen} onClose={closePreview} />
      <ConfirmDialog count={pending.length} emails={pending.map((u) => u.email)} busy={busy} onCancel={() => setPending([])} onConfirm={confirmDelete} />
    </section>
  );
}
